import React, { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Hammer, FileDown, Loader2, FlipHorizontal2 } from "lucide-react";
import { DEFAULT_WERK, LUNAR_LAMELLA, lamellaCount, lamellaHeight, profileDims, segmentsOf } from "../../lib/planner";
import { NumInput } from "../NumInput";

const numCls = "w-full px-2 py-1.5 bg-white border border-zinc-300 rounded-sm text-sm font-mono focus:border-black focus:ring-1 focus:ring-black";
const Label = ({ children }) => <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1 block">{children}</label>;

export function LamellaBox({ plan, onChange }) {
  const werk = { ...DEFAULT_WERK, ...(plan.werk || {}), lamella: { ...LUNAR_LAMELLA, ...(plan.werk?.lamella || {}) } };
  const setW = (patch) => onChange({ werk: { ...werk, ...patch } });
  const isLunar = plan.model === "LUNAR";
  const nLam = lamellaCount(plan.height, werk.lamella);
  const hLam = lamellaHeight(nLam, werk.lamella);
  return (
    <div className="bg-zinc-50 border border-zinc-200 p-3 space-y-2" data-testid="lamella-box">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600">Lamellen {isLunar ? "(LUNAR)" : "(nur LUNAR hinterlegt)"}</div>
      <div className="grid grid-cols-3 gap-2">
        <div><Label>Höhe</Label><NumInput value={werk.lamella.height} onChange={(v) => setW({ lamella: { ...werk.lamella, height: v } })} className={numCls} testId="werk-lam-height" /></div>
        <div><Label>Abstand</Label><NumInput value={werk.lamella.gap} onChange={(v) => setW({ lamella: { ...werk.lamella, gap: v } })} className={numCls} testId="werk-lam-gap" /></div>
        <div><Label>Ab Beton</Label><NumInput value={werk.lamella.bottom} onChange={(v) => setW({ lamella: { ...werk.lamella, bottom: v } })} className={numCls} testId="werk-lam-bottom" /></div>
      </div>
      <div className="text-xs text-zinc-700 flex items-center justify-between gap-2">
        <span data-testid="werk-lam-info">{nLam} Lamellen → {hLam} mm {hLam !== Number(plan.height) && <span className="text-amber-700">(Zaunhöhe {plan.height} passt nicht zum Raster {werk.lamella.height + werk.lamella.gap} mm)</span>}</span>
        {hLam !== Number(plan.height) && <button type="button" onClick={() => onChange({ height: hLam, postHeight: hLam + 60 })} className="underline whitespace-nowrap" data-testid="werk-apply-height">Höhe auf {hLam} setzen</button>}
      </div>
    </div>
  );
}

export function WerkPanel({ plan, layout, derived, onChange, quoteId, sectionLayouts, project }) {
  const werk = { ...DEFAULT_WERK, ...(plan.werk || {}), lamella: { ...LUNAR_LAMELLA, ...(plan.werk?.lamella || {}) } };
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const setW = (patch) => onChange({ werk: { ...werk, ...patch } });
  const dims = profileDims(plan.posts.field.profil);
  const drawnSegs = segmentsOf(plan);
  const isOpen = !(plan.closed && plan.points.length >= 3);
  const secList = sectionLayouts || [{ sec: plan, layout }];
  const multi = (project?.sections?.length || 1) > 1;

  const buildSection = ({ sec, layout: lay }) => {
    const sdims = profileDims(sec.posts.field.profil);
    const dsegs = segmentsOf(sec);
    const w = { ...DEFAULT_WERK, ...(sec.werk || {}), lamella: { ...LUNAR_LAMELLA, ...(sec.werk?.lamella || {}) } };
    const open = !(sec.closed && sec.points.length >= 3);
    const segments = lay.segments.map((s, i) => {
      const along = (sec.segDims && sec.segDims[s.idx]) || sec.posts.field.dim;
      const across = sdims.length === 2 ? sdims.find((d) => d !== along) ?? along : along;
      const startOffset = open && i === 0 && w.houseStart !== null && w.houseStart !== "" ? Number(w.houseStart) : Number(w.inset);
      return { label: s.label, length: s.length, outerLength: dsegs[s.idx]?.length ?? s.length, startOffset, postDim: along, postAcross: across, elements: s.elements };
    });
    return { model: sec.model, height: sec.height, lamella: w.lamella, concreteWidth: Number(w.concreteWidth) || 0, inset: Number(w.inset) || 0,
      postProfil: sec.posts.field.profil, name: sec.name, project: multi ? `${sec.name}: ${sec.model} H ${sec.height} mm` : (quoteId ? undefined : `${sec.model} H ${sec.height} mm`), segments };
  };

  const download = async () => {
    setBusy(true); setErr("");
    try {
      const sections = secList.filter((x) => x.layout.segments.length).map(buildSection);
      const r = await api.post("/planner/werkzeichnung", {
        sections, model: plan.model, height: plan.height,
        project: quoteId ? undefined : (multi ? "Zaunplan – mehrere Abschnitte" : `${plan.model} H ${plan.height} mm`),
      }, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <section className="border border-zinc-200 p-4 space-y-3" data-testid="werk-panel">
      <h2 className="text-sm font-semibold uppercase tracking-widest text-zinc-700 pb-2 border-b border-zinc-200 flex items-center gap-2"><Hammer size={14}/> Werkzeichnung / Fundament</h2>
      <label className="flex items-center gap-2 text-sm text-zinc-800 cursor-pointer">
        <input type="checkbox" checked={!!werk.enabled} onChange={(e) => setW({ enabled: e.target.checked })} className="accent-black" data-testid="werk-enabled" />
        Gezeichnete Linie = Beton-Außenkante (Pfosten eingerückt)
      </label>
      {werk.enabled && (
        <>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Betonbreite (mm)</Label><NumInput step={10} value={werk.concreteWidth} onChange={(v) => setW({ concreteWidth: v })} className={numCls} testId="werk-concrete-width" /></div>
            <div><Label>Einrückung Pfosten (mm)</Label><NumInput step={10} value={werk.inset} onChange={(v) => setW({ inset: v })} className={numCls} testId="werk-inset" /></div>
            {isOpen && (
              <>
                <div><Label>Abstand Haus – Anfang (mm)</Label><NumInput step={10} placeholder="kein Haus" emptyValue={null} value={werk.houseStart ?? null} onChange={(v) => setW({ houseStart: v })} className={numCls} testId="werk-house-start" /></div>
                <div><Label>Abstand Haus – Ende (mm)</Label><NumInput step={10} placeholder="kein Haus" emptyValue={null} value={werk.houseEnd ?? null} onChange={(v) => setW({ houseEnd: v })} className={numCls} testId="werk-house-end" /></div>
              </>
            )}
          </div>
          <button type="button" onClick={() => setW({ side: -(werk.side || 1) })} data-testid="werk-flip-side"
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 border border-zinc-300 rounded-sm hover:border-zinc-900"><FlipHorizontal2 size={12}/> Betonseite wechseln</button>

          <div>
            <Label>Pfostenmaß in Zaunrichtung je Segment ({plan.posts.field.profil})</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="werk-seg-dims">
              {layout.segments.map((s) => {
                const cur = (plan.segDims && plan.segDims[s.idx]) || plan.posts.field.dim;
                return (
                  <div key={s.idx} className="flex items-center border border-zinc-300 rounded-sm overflow-hidden text-xs">
                    <span className="px-2 font-bold bg-zinc-100">{s.label}</span>
                    {dims.map((d) => (
                      <button key={d} type="button" onClick={() => onChange({ segDims: { ...(plan.segDims || {}), [s.idx]: d } })} data-testid={`werk-seg-${s.idx}-dim-${d}`}
                              className={`px-2 py-1 font-mono ${cur === d ? "bg-zinc-900 text-white" : "bg-white text-zinc-700"}`}>{d}</button>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <LamellaBox plan={plan} onChange={onChange} />

          <button onClick={download} disabled={busy || !secList.some((x) => x.layout.segments.length)} data-testid="werk-pdf-button"
                  className="w-full border border-zinc-900 text-zinc-900 text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-zinc-900 hover:text-white disabled:opacity-40 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <FileDown size={14}/>} Werkzeichnung PDF{multi ? " – alle Abschnitte" : ""} (A4 quer)
          </button>
          {err && <div className="text-xs text-red-600" data-testid="werk-error">{err}</div>}
          <p className="text-[11px] text-zinc-500">Zaunlinie = Außenkante minus Einrückung; an offenen Enden beginnt der erste Pfosten im Abstand „Haus“ (sonst Einrückung). Feldbreiten = Lichte zwischen Pfosten.</p>
        </>
      )}
    </section>
  );
}
